
import {
  normalizeOperationalBoolean,
  normalizeOperationalProduct,
} from "../services/restaurantCatalog";

export default {
  name: "ConfiguradorItemRestaurante",
  props: {
    compositionItem: {
      type: Object,
      default: null,
    },
    product: {
      type: Object,
      default: null,
    },
    showFinancial: {
      type: Boolean,
      default: true,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    quote: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      quantity: 1,
      observation: "",
      selectedSize: null,
      selectedFlavors: [],
      selectedBorder: null,
      selections: {},
      optionQuantities: {},
      errors: [],
      selectedComboId: null,
    };
  },
  computed: {
    combos() {
      if (this.compositionItem) return [];
      return this.product?.combos?.length
        ? this.product.combos
        : this.product?.combo
        ? [this.product.combo]
        : [];
    },
    selectedCombo() {
      return (
        this.combos.find(
          (combo) => String(combo.id) === String(this.selectedComboId)
        ) || null
      );
    },
    comboItems() {
      return this.selectedCombo?.itens || [];
    },
    comboItemProducts() {
      return this.comboItems.reduce((products, item) => {
        if (item.produto) {
          products[item.chave] = normalizeOperationalProduct({
            ...item.produto,
            combo: null,
            combos: [],
          });
        }
        return products;
      }, {});
    },
    fixedSize() {
      return (
        this.compositionItem?.pizza_tamanho_slug ||
        this.selectedCombo?.pizza_tamanho_slug ||
        null
      );
    },
    pizza() {
      return this.product?.pizza || null;
    },
    pizzaSizes() {
      const sizes = this.pizza?.tamanhos || this.pizza?.sizes || [];
      return sizes.filter(
        (size) =>
          this.asBoolean(size.ativo, true) &&
          this.asBoolean(size.active, true) &&
          this.asBoolean(size.disponivel, true) &&
          this.asBoolean(size.available, true)
      );
    },
    selectedSizeObject() {
      return (
        this.pizzaSizes.find(
          (size) => this.sizeKey(size) === this.selectedSize
        ) || null
      );
    },
    maxPizzaFlavors() {
      return Math.min(
        4,
        Math.max(
          1,
          Number(
            this.selectedSizeObject?.max_sabores ||
              this.selectedSizeObject?.max_flavors ||
              this.pizza?.max_sabores_padrao ||
              1
          ) || 1
        )
      );
    },
    availablePizzaFlavors() {
      const flavors = this.pizza?.sabores || this.pizza?.flavors || [];
      if (!this.selectedSize) {
        return [];
      }
      return flavors.filter(
        (flavor) =>
          this.asBoolean(flavor.ativo, true) &&
          this.asBoolean(flavor.active, true) &&
          this.asBoolean(flavor.disponivel, true) &&
          this.asBoolean(flavor.available, true) &&
          (!this.compositionItem?.pizza_sabores_slugs?.length ||
            this.compositionItem.pizza_sabores_slugs.includes(
              this.optionKey(flavor)
            )) &&
          this.optionSupportsSelectedSize(flavor)
      );
    },
    availablePizzaBorders() {
      const borders = this.pizza?.bordas || this.pizza?.borders || [];
      if (!this.selectedSize) {
        return [];
      }
      return borders.filter(
        (border) =>
          this.asBoolean(border.ativo, true) &&
          this.asBoolean(border.active, true) &&
          this.asBoolean(border.disponivel, true) &&
          this.asBoolean(border.available, true) &&
          this.optionSupportsSelectedSize(border)
      );
    },
    optionGroups() {
      const groups = Array.isArray(this.product?.grupos_opcoes)
        ? this.product.grupos_opcoes
        : [];
      return groups.filter(
        (group) =>
          this.asBoolean(group.ativo, true) &&
          this.asBoolean(group.active, true) &&
          this.asBoolean(group.disponivel, true) &&
          this.asBoolean(group.available, true)
      );
    },
    quoteTotalCents() {
      return Math.max(
        0,
        Number(
          this.quote?.valor_total_centavos ??
            this.quote?.total_centavos ??
            Math.round(
              Number(this.quote?.valor_total ?? this.quote?.total ?? 0) * 100
            )
        ) || 0
      );
    },
  },
  watch: {
    product: {
      immediate: true,
      handler() {
        this.reset();
      },
    },
  },
  methods: {
    asBoolean(value, fallback = false) {
      return normalizeOperationalBoolean(value, fallback);
    },
    reset() {
      this.quantity = 1;
      this.observation = "";
      this.selectedFlavors = [];
      this.selectedBorder = null;
      this.selections = {};
      this.optionQuantities = {};
      this.errors = [];
      this.selectedComboId =
        this.product?.combo?.id || this.combos[0]?.id || null;
      this.resetComboConfiguration();
    },
    resetComboConfiguration() {
      this.selectedFlavors = [];
      this.selectedBorder = null;
      this.selectedSize =
        this.fixedSize ||
        (this.pizzaSizes.length === 1
          ? this.sizeKey(this.pizzaSizes[0])
          : null);
    },
    submit() {
      this.errors = this.validate();
      if (this.errors.length) {
        return;
      }

      this.$emit("submit", this.buildPayload());
    },
    buildPayload() {
      const payload = {
        produto_id: this.product.produto_id || this.product.id,
        cardapio_produto_id: this.product.cardapio_produto_id || null,
        quantidade: Math.max(1, Math.min(99, Number(this.quantity) || 1)),
        observacao: this.observation || null,
        adicionais: this.buildAdditions(),
      };

      if (this.selectedCombo) {
        payload.combo_id = this.selectedCombo.id;
        const configurators = this.$refs.comboConfigurators || [];
        payload.combo_itens = this.comboItems.map((item) => {
          const child = configurators.find(
            (configurator) => configurator.compositionItem.chave === item.chave
          );
          const configuration = child.buildPayload();
          return {
            chave: item.chave,
            produto: item.produto_slug,
            adicionais: configuration.adicionais,
            ...(configuration.pizza ? { pizza: configuration.pizza } : {}),
          };
        });
      }

      if (this.pizza) {
        const size = this.selectedSizeObject;
        payload.pizza = {
          tamanho: size?.id ?? size?.codigo ?? this.selectedSize,
          sabores: this.selectedFlavors.slice(),
          borda: this.selectedBorder || null,
        };
      }

      return payload;
    },
    validate() {
      const errors = [];
      const configurators = this.$refs.comboConfigurators || [];
      this.comboItems.forEach((item) => {
        const child = configurators.find(
          (configurator) => configurator.compositionItem.chave === item.chave
        );
        if (!item.produto || !child) {
          errors.push(
            `${
              item.nome || item.produto_nome || item.produto_slug
            }: item do combo indisponível.`
          );
          return;
        }
        child.errors = child.validate();
        child.errors.forEach((error) =>
          errors.push(
            `${item.nome || item.produto_nome || item.produto_slug}: ${error}`
          )
        );
      });
      if (
        !Number.isFinite(Number(this.quantity)) ||
        Number(this.quantity) < 1
      ) {
        errors.push("Informe uma quantidade válida.");
      }
      if (this.pizza && !this.selectedSizeObject) {
        errors.push("Escolha o tamanho da pizza.");
      }
      if (
        this.pizza &&
        this.selectedSizeObject &&
        !this.selectedFlavors.length
      ) {
        errors.push("Escolha ao menos um sabor.");
      }
      if (
        this.pizza &&
        new Set(this.selectedFlavors).size !== this.selectedFlavors.length
      ) {
        errors.push("Não é permitido repetir o mesmo sabor.");
      }
      if (this.pizza && this.selectedFlavors.length > this.maxPizzaFlavors) {
        errors.push(`Escolha no máximo ${this.maxPizzaFlavors} sabores.`);
      }
      if (
        this.pizza &&
        this.selectedBorder &&
        !this.availablePizzaBorders.some(
          (border) => this.optionKey(border) === this.selectedBorder
        )
      ) {
        errors.push("Escolha uma borda disponível para o tamanho selecionado.");
      }

      this.optionGroups.forEach((group) => {
        const count = this.groupSelectedCount(group);
        const minimum = this.groupMinimum(group);
        const maximum = this.groupMaximum(group);
        if (count < minimum) {
          errors.push(
            this.isTextGroup(group)
              ? `${this.groupName(group)}: preencha este campo.`
              : `${this.groupName(group)}: escolha pelo menos ${minimum} opção${
                  minimum === 1 ? "" : "ões"
                }.`
          );
        }
        if (maximum && count > maximum) {
          errors.push(
            `${this.groupName(group)}: escolha no máximo ${maximum} opção${
              maximum === 1 ? "" : "ões"
            }.`
          );
        }
      });
      return errors;
    },
    buildAdditions() {
      const additions = [];
      this.optionGroups.forEach((group) => {
        const value = this.selection(group);
        if (this.isTextGroup(group)) {
          if (String(value || "").trim()) {
            additions.push({
              grupo: group.id ?? group.codigo,
              texto: String(value).trim(),
            });
          }
          return;
        }
        const values = Array.isArray(value) ? value : value ? [value] : [];
        values.forEach((selectedKey) => {
          const option = this.groupOptions(group).find(
            (item) => this.optionKey(item) === selectedKey
          );
          if (!option) {
            return;
          }
          additions.push({
            grupo: group.id ?? group.codigo,
            item: option.id ?? option.codigo,
            quantidade: this.optionQuantity(group, option),
          });
        });
      });
      return additions;
    },
    selection(group) {
      const value = this.selections[this.groupKey(group)];
      if (value !== undefined) {
        return value;
      }
      return this.isSingleGroup(group) || this.isTextGroup(group) ? "" : [];
    },
    setTextSelection(group, value) {
      this.$set(this.selections, this.groupKey(group), value);
    },
    clearOptionSelection(group) {
      const groupKey = this.groupKey(group);
      const selected = this.selection(group);
      const selectedKeys = Array.isArray(selected)
        ? selected
        : selected
        ? [selected]
        : [];

      selectedKeys.forEach((optionKey) => {
        const option = this.groupOptions(group).find(
          (item) => this.optionKey(item) === optionKey
        );
        if (option) {
          this.$delete(
            this.optionQuantities,
            this.optionQuantityKey(group, option)
          );
        }
      });

      this.$set(this.selections, groupKey, this.isSingleGroup(group) ? "" : []);
    },
    toggleOption(group, option) {
      const groupKey = this.groupKey(group);
      const optionKey = this.optionKey(option);
      if (this.isSingleGroup(group)) {
        const current = this.selection(group);
        if (current && current !== optionKey) {
          this.$delete(
            this.optionQuantities,
            this.optionQuantityKey(group, { id: current })
          );
        }
        this.$set(this.selections, groupKey, optionKey);
        this.$set(
          this.optionQuantities,
          this.optionQuantityKey(group, option),
          1
        );
        return;
      }
      const current = Array.isArray(this.selections[groupKey])
        ? this.selections[groupKey].slice()
        : [];
      const index = current.indexOf(optionKey);
      if (index >= 0) {
        current.splice(index, 1);
        this.$delete(
          this.optionQuantities,
          this.optionQuantityKey(group, option)
        );
      } else if (
        !this.groupMaximum(group) ||
        this.groupSelectedCount(group) < this.groupMaximum(group)
      ) {
        current.push(optionKey);
        this.$set(
          this.optionQuantities,
          this.optionQuantityKey(group, option),
          1
        );
      }
      this.$set(this.selections, groupKey, current);
    },
    isOptionSelected(group, option) {
      const current = this.selection(group);
      return Array.isArray(current)
        ? current.includes(this.optionKey(option))
        : current === this.optionKey(option);
    },
    isOptionDisabled(group, option) {
      if (this.isSingleGroup(group) || this.isOptionSelected(group, option)) {
        return false;
      }
      const maximum = this.groupMaximum(group);
      return Boolean(maximum && this.groupSelectedCount(group) >= maximum);
    },
    groupAllowsQuantity(group) {
      return (
        this.asBoolean(group.permite_quantidade) ||
        this.asBoolean(group.allowQuantity)
      );
    },
    optionQuantityKey(group, option) {
      return `${this.groupKey(group)}:${this.optionKey(option)}`;
    },
    optionQuantity(group, option) {
      if (!this.groupAllowsQuantity(group)) {
        return 1;
      }
      return Math.max(
        1,
        Number(
          this.optionQuantities[this.optionQuantityKey(group, option)] || 1
        )
      );
    },
    groupSelectedCount(group) {
      const value = this.selection(group);
      if (!Array.isArray(value)) {
        return String(value || "").trim() ? 1 : 0;
      }
      if (!this.groupAllowsQuantity(group)) {
        return value.length;
      }
      return value.reduce((total, selectedKey) => {
        const option = this.groupOptions(group).find(
          (item) => this.optionKey(item) === selectedKey
        );
        return total + (option ? this.optionQuantity(group, option) : 0);
      }, 0);
    },
    canIncreaseOptionQuantity(group, option) {
      const maximum = this.groupMaximum(group);
      return !maximum || this.groupSelectedCount(group) < maximum;
    },
    changeOptionQuantity(group, option, delta) {
      if (
        !this.groupAllowsQuantity(group) ||
        !this.isOptionSelected(group, option)
      ) {
        return;
      }
      const current = this.optionQuantity(group, option);
      const next = Math.max(1, Math.min(99, current + Number(delta || 0)));
      if (next > current && !this.canIncreaseOptionQuantity(group, option)) {
        return;
      }
      this.$set(
        this.optionQuantities,
        this.optionQuantityKey(group, option),
        next
      );
    },
    toggleFlavor(flavor) {
      const key = this.optionKey(flavor);
      const index = this.selectedFlavors.indexOf(key);
      if (index >= 0) {
        this.selectedFlavors.splice(index, 1);
      } else if (this.selectedFlavors.length < this.maxPizzaFlavors) {
        this.selectedFlavors.push(key);
      }
    },
    trimPizzaFlavors() {
      const available = new Set(this.availablePizzaFlavors.map(this.optionKey));
      this.selectedFlavors = this.selectedFlavors
        .filter((id) => available.has(id))
        .slice(0, this.maxPizzaFlavors);
      const borders = new Set(this.availablePizzaBorders.map(this.optionKey));
      if (this.selectedBorder && !borders.has(this.selectedBorder)) {
        this.selectedBorder = null;
      }
    },
    optionSupportsSelectedSize(option) {
      const sizes =
        option.tamanhos || option.tamanho_ids || option.available_size_ids;
      if (!Array.isArray(sizes) || !sizes.length) {
        return true;
      }
      return sizes
        .map((size) => size?.id ?? size?.codigo ?? size)
        .map(String)
        .includes(String(this.selectedSize));
    },
    isFlavorSelected(flavor) {
      return this.selectedFlavors.includes(this.optionKey(flavor));
    },
    groupOptions(group) {
      const options = group.itens || group.opcoes || group.options || [];
      return options.filter(
        (option) =>
          this.asBoolean(option.ativo, true) &&
          this.asBoolean(option.active, true) &&
          this.asBoolean(option.disponivel, true) &&
          this.asBoolean(option.available, true)
      );
    },
    groupKey(group) {
      return String(group.id ?? group.codigo ?? group.nome);
    },
    groupName(group) {
      return group.nome || group.name || "Opções";
    },
    groupType(group) {
      return group.tipo || group.type || "multipla_escolha";
    },
    isTextGroup(group) {
      return this.groupType(group) === "observacao_textual";
    },
    isSingleGroup(group) {
      return ["escolha_unica", "acompanhamento_obrigatorio"].includes(
        this.groupType(group)
      );
    },
    groupRequired(group) {
      return (
        this.asBoolean(group.obrigatorio) ||
        this.asBoolean(group.required) ||
        this.groupType(group) === "acompanhamento_obrigatorio" ||
        Number(group.minimo ?? group.min ?? 0) > 0
      );
    },
    groupMinimum(group) {
      if (group.minimo !== undefined || group.min !== undefined) {
        return Math.max(0, Number(group.minimo ?? group.min) || 0);
      }
      return this.groupRequired(group) ? 1 : 0;
    },
    groupMaximum(group) {
      if (this.isSingleGroup(group)) {
        return 1;
      }
      return Math.max(0, Number(group.maximo ?? group.max ?? 0) || 0);
    },
    groupMaxLength(group) {
      return Math.max(1, Number(group.max_caracteres || 500));
    },
    groupRule(group) {
      if (this.isTextGroup(group)) {
        return this.groupRequired(group)
          ? "Preenchimento obrigatório"
          : "Preenchimento opcional";
      }
      const minimum = this.groupMinimum(group);
      const maximum = this.groupMaximum(group);
      if (minimum && maximum) {
        return `Escolha de ${minimum} a ${maximum}`;
      }
      if (minimum) {
        return `Escolha pelo menos ${minimum}`;
      }
      if (maximum) {
        return `Escolha até ${maximum}`;
      }
      return "Opcional";
    },
    sizeKey(size) {
      return String(size.id ?? size.codigo ?? size.nome);
    },
    optionKey(option) {
      return String(option.id ?? option.codigo ?? option.nome);
    },
    optionName(option) {
      return option.nome || option.name || option.descricao || "Opção";
    },
    optionPriceCents(option) {
      const cents = Number(
        option.preco_adicional_centavos ??
          option.preco_centavos ??
          option.preco_base_centavos ??
          Math.round(Number(option.preco_base ?? option.preco ?? 0) * 100)
      );
      return Number.isFinite(cents) ? cents : 0;
    },
    flavorPriceCents(flavor) {
      const prices = flavor.precos || flavor.preco_por_tamanho || {};
      const selectedPrice = prices[this.selectedSize];
      if (selectedPrice !== undefined) {
        return Number(selectedPrice.preco_centavos ?? selectedPrice ?? 0);
      }
      return this.optionPriceCents(flavor);
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
  },
};
